import React from 'react';

import {
  DateField, RecordContextProvider, useRecordContext,
} from 'react-admin';

import {
  Box,
  Grid,
  Card,
  CardHeader,
  Divider,
  CardContent,
  Typography,
  Link,
} from '@mui/material';

import { isEmpty } from 'lodash';

import { SanctionListMatches } from '../../utilities/schemas/sanctionListMatches';
import getBaseUrl from '../../utilities/getBaseUrl';

import SanctionTitle from './SanctionTitle';

type SourceDetails = SanctionListMatches['potentialMatches'][number]['sourceDetails'][number];

const Source = () => {
  const record = useRecordContext<SourceDetails>();

  const title = isEmpty(record?.title) ? 'Unknown title' : record!.title;
  const description = isEmpty(record?.description) ? 'No description' : record!.description;

  const enableDocumentLink = !isEmpty(record?.documentLink);
  const enableLink = !isEmpty(record?.link);

  return (
    <Box display="flex" flexDirection="column">
      <Box display="flex" alignItems="baseline" gap={2}>
        <SanctionTitle>
          {title}
        </SanctionTitle>
        {record?.datePublished && (
          <Box display="flex" alignItems="baseline" gap={1}>
            <Typography variant="label" sx={{ fontSize: 13 }}>
              Date published:
            </Typography>
            <DateField
              sx={{
                fontSize: 13,
              }}
              variant="label"
              source="datePublished"
              emptyText="-"
            />
          </Box>
        )}
        {record?.dateAdded && (
          <Box display="flex" alignItems="baseline" gap={1}>
            <Typography variant="label" sx={{ fontSize: 13 }}>
              Date captured:
            </Typography>
            <DateField
              sx={{
                fontSize: 13,
              }}
              variant="label"
              source="dateAdded"
              emptyText="-"
            />
          </Box>
        )}
      </Box>
      <Typography variant="body2">
        {description}
      </Typography>
      {enableDocumentLink && (
        <Link
          href={record!.documentLink}
          color="primary"
          target="_blank"
          rel="noopener noreferrer"
        >
          PDF link
        </Link>
      )}
      {enableLink && (
        <Link
          href={record!.link}
          color="primary"
          target="_blank"
          rel="noopener noreferrer"
        >
          {getBaseUrl(record!.link!)}
        </Link>
      )}
    </Box>
  );
};

const SanctionSources = () => {
  const record = useRecordContext<SanctionListMatches['potentialMatches'][number]>();

  if (!record) {
    return null;
  }

  if (isEmpty(record.sourceDetails)) {
    return null;
  }

  return (
    <Grid size={12}>
      <Card variant="outlined" sx={{ margin: 0 }}>
        <CardHeader title="Sources" />
        <Divider />
        <CardContent>
          <Box display="flex" gap={6} flexDirection="column">
            {record.sourceDetails.map((source) => (
              <RecordContextProvider key={source.title} value={source}>
                <Source />
              </RecordContextProvider>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default SanctionSources;
